import { useCallback, useEffect, useState } from 'react'

import { videoApi } from '@carrent/api'
import { useLiveData } from '@carrent/core'

/**
 * Видеозвонки: доменные константы и хуки.
 *
 * Живёт в domain, а не в консоли: водительскому приложению нужен
 * тот же список «мои звонки» и тот же вход по токену, различается
 * только композиция экранов.
 */

export const CALL_STATUSES = {
  scheduled: { label: 'Запланирован', tone: 'info' },
  active: { label: 'Идёт', tone: 'success' },
  ended: { label: 'Завершён', tone: 'muted' },
  revoked: { label: 'Отозван', tone: 'danger' },
  expired: { label: 'Истёк', tone: 'muted' },
}

export const CALL_PRIVACIES = {
  open: {
    label: 'Открытая ссылка',
    hint: 'Подключится любой, у кого есть ссылка',
  },
  password: {
    label: 'По паролю',
    hint: 'Ссылка плюс пароль; своим пароль не нужен',
  },
  invite: {
    label: 'По приглашению',
    hint: 'Персональные ссылки; остальные — в лобби',
  },
}

/** Звонок ещё принимает участников. */
export const isJoinable = (call) =>
  call && (call.status === 'scheduled' || call.status === 'active')

export const VIDEO_CALL_EVENTS = [
  'video.call.created',
  'video.call.started',
  'video.call.ended',
  'video.call.revoked',
]

/** Запланированные звонки месяца, разложенные по дням (YYYY-MM-DD). */
export function useVideoCalendar({ from, to, enabled = true } = {}) {
  const [byDay, setByDay] = useState({})

  useEffect(() => {
    if (!enabled || !from || !to) return undefined
    let alive = true
    videoApi.calendar(from, to)
      .then(({ data }) => {
        if (!alive) return
        const map = {}
        for (const call of data.data || []) {
          if (!call.scheduled_at) continue
          // Ключ дня в локальной зоне — календарь рисует по местному времени.
          const d = new Date(call.scheduled_at)
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          ;(map[key] = map[key] || []).push(call)
        }
        setByDay(map)
      })
      .catch(() => alive && setByDay({}))
    return () => { alive = false }
  }, [from, to, enabled])

  const callsOn = useCallback((date) => {
    const d = new Date(date)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return byDay[key] || []
  }, [byDay])

  return { byDay, callsOn }
}

/** Список моих/доступных звонков; живёт на событиях шины. */
export function useVideoCalls({ status, enabled = true } = {}) {
  const load = useCallback(async () => {
    const { data } = await videoApi.list(status)
    return data.data
  }, [status])

  const { data, loading, error, refresh, isLive } = useLiveData({
    load,
    eventTypes: VIDEO_CALL_EVENTS,
    enabled,
  })

  return { items: data || [], loading, error, refresh, isLive }
}

/** Карточка звонка. 404 отдаём наружу как notFound: чужой звонок «не существует». */
export function useVideoCall(id) {
  const [call, setCall] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const refresh = useCallback(async () => {
    if (!id) return
    try {
      const { data } = await videoApi.get(id)
      setCall(data.data)
      setNotFound(false)
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true)
      else throw e
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    setLoading(true)
    refresh().catch(() => setLoading(false))
  }, [refresh])

  return { call, loading, notFound, refresh, setCall }
}

/**
 * Публичные настройки модуля (enabled, кто создаёт, интеграции).
 *
 * Fallback — «всё выключено»: пока настройки не доехали, кнопок
 * звонков не видно. Мигнуть отсутствием кнопки лучше, чем показать
 * кнопку, ведущую в 503.
 */
export const VIDEO_SETTINGS_FALLBACK = {
  enabled: false,
  managers_can_create: false,
  drivers_can_create: false,
  guest_access: false,
  default_privacy: 'password',
  default_ttl_hours: 4,
  call_from_chat: false,
  call_from_trip: false,
  call_from_ticket: false,
}

let settingsCache = null
let settingsInflight = null

export function resetVideoSettingsCache() {
  settingsCache = null
  settingsInflight = null
}

export async function fetchVideoSettings() {
  if (settingsCache) return settingsCache
  if (!settingsInflight) {
    settingsInflight = videoApi
      .settings()
      .then(({ data }) => {
        settingsCache = { ...VIDEO_SETTINGS_FALLBACK, ...(data.data || {}) }
        return settingsCache
      })
      .catch(() => VIDEO_SETTINGS_FALLBACK)
      .finally(() => {
        settingsInflight = null
      })
  }
  return settingsInflight
}

export function useVideoSettings() {
  const [settings, setSettings] = useState(settingsCache || VIDEO_SETTINGS_FALLBACK)
  const [loading, setLoading] = useState(!settingsCache)

  useEffect(() => {
    let alive = true
    fetchVideoSettings().then((value) => {
      if (!alive) return
      setSettings(value)
      setLoading(false)
    })
    return () => {
      alive = false
    }
  }, [])

  const canCreate = useCallback(
    (role) => {
      if (!settings.enabled) return false
      if (role === 'admin') return true
      if (role === 'manager') return !!settings.managers_can_create
      return !!settings.drivers_can_create
    },
    [settings],
  )

  return { settings, loading, canCreate }
}
